import { useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import RichEditor from "@/components/RichEditor";
import ImageUploadField from "@/components/ImageUploadField";
import EmailPreviewModal from "@/components/EmailPreviewModal";

const emptyPost = {
  title: "", excerpt: "", content: "", cover_image: "",
  tag: "Reflections", published: true, locked: false, publish_at: "",
};

/** Convert an ISO datetime string to a "YYYY-MM-DDTHH:MM" string for datetime-local inputs. */
function toDatetimeLocal(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

/** Format a UTC ISO string as a readable local date-time for display labels. */
function formatSchedule(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function isScheduledFuture(iso) {
  if (!iso) return false;
  try { return new Date(iso) > new Date(); } catch { return false; }
}

export default function PostsAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = () =>
    api.get("/posts", { params: { include_unpublished: true } }).then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyPost); setEditing("new"); };
  const startEdit = (p) => {
    setForm({
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      cover_image: p.cover_image,
      tag: p.tag || "Reflections",
      published: !!p.published,
      locked: !!p.locked,
      publish_at: toDatetimeLocal(p.publish_at),
    });
    setEditing(p.id);
  };
  const cancel = () => { setEditing(null); setForm(emptyPost); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // datetime-local value has no tz — send as-is; backend normalises to UTC
      if (editing === "new") await api.post("/posts", payload);
      else await api.put(`/posts/${editing}`, payload);
      toast.success("Entry saved");
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save."));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      console.error("Failed to delete post:", err);
      toast.error("Could not delete");
    }
  };

  const previewPayload = useMemo(() => ({
    title: form.title,
    excerpt: form.excerpt,
    cover_image: form.cover_image,
    slug: (form.title || "preview").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80) || "preview",
  }), [form.title, form.excerpt, form.cover_image]);

  // Whether this post is going out to subscribers immediately vs scheduled
  const isScheduled = !!form.publish_at && isScheduledFuture(new Date(form.publish_at).toISOString().replace("T", "T").slice(0, 19) + "Z");

  return (
    <AdminLayout title="Journal">
      <Toaster position="top-center" />
      <div className="flex justify-between items-center flex-wrap gap-4">
        <p className="text-[#5C6670] font-light">Letters, essays, reflections — committed to the page.</p>
        <button onClick={startNew} data-testid="post-new" className="gv-btn-gold">+ New entry</button>
      </div>

      {editing && (
        <div data-testid="post-form" className="mt-8 bg-white border border-[#C5A059] p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="gv-label">Title</label>
            <input className="gv-input mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" />
          </div>
          <div>
            <label className="gv-label">Tag</label>
            <input className="gv-input mt-2" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
          </div>

          {/* Publish options */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-3 text-sm text-[#1A1F24]">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="post-published"
              />
              <span>Published immediately</span>
            </label>

            <label className="flex items-center gap-3 text-sm text-[#1A1F24]">
              <input
                type="checkbox"
                checked={form.locked}
                onChange={(e) => setForm({ ...form, locked: e.target.checked })}
                data-testid="post-locked"
              />
              <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#C5A059]" /> Exclusive — members only</span>
            </label>

            <div>
              <label className="gv-label text-[0.65rem]">
                Schedule for later <span className="text-[#B8BCC2] font-normal normal-case">(optional — overrides published)</span>
              </label>
              <input
                type="datetime-local"
                className="gv-input mt-1 text-sm"
                value={form.publish_at}
                onChange={(e) => setForm({ ...form, publish_at: e.target.value })}
                data-testid="post-publish-at"
              />
              {isScheduled && (
                <p className="mt-1 text-xs text-[#C5A059]">
                  Will go live on {formatSchedule(new Date(form.publish_at).toISOString())} and notify subscribers.
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <ImageUploadField
              value={form.cover_image}
              onChange={(url) => setForm({ ...form, cover_image: url })}
              label="Cover image"
              testIdPrefix="post-cover"
              aspect="aspect-[4/3]"
            />
            <input type="hidden" data-testid="post-cover-image" value={form.cover_image || ""} readOnly />
          </div>
          <div className="md:col-span-2">
            <label className="gv-label">Excerpt</label>
            <textarea rows={2} className="gv-input mt-2 resize-none" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="post-excerpt" />
          </div>
          <div className="md:col-span-2">
            <label className="gv-label">Content</label>
            <div className="mt-2">
              <RichEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} />
            </div>
            <input type="hidden" data-testid="post-content" value={form.content} readOnly />
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end">
            <button onClick={cancel} className="gv-btn-outline" data-testid="post-cancel">Cancel</button>
            <button
              onClick={() => setPreviewOpen(true)}
              disabled={!form.title.trim()}
              className="gv-btn-outline"
              data-testid="post-preview-email"
              title={!form.title.trim() ? "Add a title first" : "Preview the email subscribers will receive"}
            >
              Preview email
            </button>
            <button onClick={save} disabled={saving} className="gv-btn-gold" data-testid="post-save">
              {saving ? "Saving…" : isScheduled ? "Schedule entry" : "Save entry"}
            </button>
          </div>
        </div>
      )}

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        type="post"
        payload={previewPayload}
      />

      <div className="mt-12 divide-y divide-[#E5E5E5]">
        {posts.map((p) => {
          const scheduled = isScheduledFuture(p.publish_at);
          return (
            <div key={p.id} data-testid={`admin-post-${p.id}`} className="py-5 flex items-start gap-4">
              <div className="w-16 h-16 flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
                {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs tracking-[0.2em] uppercase text-[#C5A059] flex items-center gap-2">
                  <span>{p.tag}</span>
                  {scheduled ? (
                    <span data-testid={`post-scheduled-badge-${p.id}`} className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-sm">
                      Scheduled · {formatSchedule(p.publish_at)}
                    </span>
                  ) : !p.published ? (
                    <span className="text-[#B8BCC2]">· Draft</span>
                  ) : null}
                </div>
                <div className="font-serif text-xl text-[#1A1F24] mt-1">{p.title}</div>
                <p className="text-sm text-[#5C6670] mt-1 line-clamp-2">{p.excerpt}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <button onClick={() => startEdit(p)} className="text-xs tracking-[0.2em] uppercase text-[#C5A059] gv-link" data-testid={`admin-post-edit-${p.id}`}>Edit</button>
                <button onClick={() => remove(p.id)} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-red-500" data-testid={`admin-post-delete-${p.id}`}>Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
